import defineBindingContexts from './binding-contexts';
import defineBindingContextTypes from './binding-context-types';
import defineBindableProperties from './bindable-properties';
import defineRelationships from './relationships';

import type {
  DataSourceDefinition,
  DataSourceDependencies,
  TypeReferenceBuilder,
  BindingContextDef,
  BindingContextRelationshipDef,
  BindablePropertiesDef,
} from '@packages/systems/dynamo/source-types';
import {isCommerceCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {Plugins} from '@packages/systems/wfdl';
import {singleMemo} from '@packages/utilities/memo';
import {sortCollectionsBySortPos} from '@packages/systems/dynamo/utils/CollectionUtils';
import type {Collections} from '@packages/systems/dynamo/records/CollectionRecord';

const onlyCmsCollections = singleMemo(
  (collections: Collections): Collections =>
    collections.filterNot(isCommerceCollection)
);

function getCmsCollections(dependencies: DataSourceDependencies) {
  const collections = dependencies.getCollections();

  if (!collections) {
    return null;
  }

  let cmsCollections = onlyCmsCollections(collections);

  if (dependencies.runtimeEnvironment.isDesigner()) {
    // sort collections only in Designer environment for UI reasons
    cmsCollections = sortCollectionsBySortPos(cmsCollections);
  }

  return cmsCollections;
}

export const dataSourceDefinition: DataSourceDefinition = {
  pluginName: Plugins.name('CMS'),
  defineTypes(dependencies: DataSourceDependencies) {
    const cmsCollections = getCmsCollections(dependencies);
    return cmsCollections && cmsCollections.size > 0
      ? defineBindingContextTypes(cmsCollections)
      : [];
  },

  defineBindingContexts<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
    const cmsCollections = getCmsCollections(dependencies);
    return cmsCollections && cmsCollections.size > 0
      ? defineBindingContexts(cmsCollections, referenceBuilder)
      : [];
  },

  defineRelationships<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindingContextRelationshipDef<RootTRef, EntityTRef>[] {
    const cmsCollections = getCmsCollections(dependencies);
    return cmsCollections && cmsCollections.size > 0
      ? defineRelationships(cmsCollections, referenceBuilder)
      : [];
  },

  defineBindableProperties<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindablePropertiesDef<EntityTRef, ListTRef>[] {
    const cmsCollections = getCmsCollections(dependencies);
    const isDesigner = dependencies.runtimeEnvironment.isDesigner();
    return cmsCollections && cmsCollections.size > 0
      ? defineBindableProperties(cmsCollections, referenceBuilder, isDesigner)
      : [];
  },
};
